import React from "react";
import { COULEUR, PhraseAbsence } from "./_commun";

// Consignes — ce qu'il a retenu, avec la date et la vie de chaque consigne.
//
// Ce qu'il apprend (registre) : « ce qu'il a retenu, avec la date et la vie
// de chaque consigne ». Dimitri, 2026-09-13 : « ses consignes, avec leur
// date, le nombre de fois où il les a appliquées, et une croix pour lui faire
// oublier ».
//
// ══ FORME DES DONNÉES ═══════════════════════════════════════════════════════
//
//   {
//     consignes: [
//       { id:           "uuid",
//         texte:        "le ménage de lc3 se fait le matin",
//         pose_le:      "2026-09-08T09:12:00.000Z",
//         pose_le_heure:"mar. 8 sept., 11:12",     // l'heure de Paris, rendue serveur
//         appliquee:    3 | null,                 // ⚠️ null ≠ 0 : « on ne sait pas »
//         derniere_fois:"…" | null,
//         portee:       { type: "logement"|"proprio"|"partout", id: "lc3"|null },
//         verrouillee:  false },
//       …
//     ],
//     compte_existe: false,                       // le compte d'application est-il vivant ?
//     compte_motif:  "le compte n'existe pas encore : …" | null
//   }
//
// ⚠️ `appliquee: null` se rend « pas encore compté », JAMAIS « 0 fois ». Un
// zéro inventé se lirait « jamais servi » alors qu'on ne sait pas — c'est
// précisément le défaut que ce dépôt traque.
//
// ⚠️ LA CROIX N'OUBLIE PAS ELLE-MÊME : elle rend l'INTENTION `onOublier(id)` ;
// c'est l'écran qui appelle le levier existant (`operateur_memoire_decide`,
// geste `oublier`). Sans `onOublier`, pas de croix — une croix qui ne fait
// rien est une promesse non tenue.

const CONSIGNES_MAX = 30;

function estTexte(s, max) {
  return typeof s === "string" && s.trim().length > 0 && s.length <= max;
}

function validerConsignes(d) {
  if (!d || typeof d !== "object" || !Array.isArray(d.consignes))
    return { erreur: "pas de consignes à montrer" };
  if (d.consignes.length === 0) return { vide: true };
  const consignes = [];
  for (const c of d.consignes.slice(0, CONSIGNES_MAX)) {
    if (
      !c ||
      typeof c !== "object" ||
      !estTexte(c.id, 80) ||
      !estTexte(c.texte, 600)
    )
      return {
        erreur:
          "données incomplètes — une consigne n'a ni texte ni identifiant",
      };
    consignes.push({
      id: c.id,
      texte: c.texte.trim(),
      pose_le_heure: estTexte(c.pose_le_heure, 40) ? c.pose_le_heure : null,
      appliquee:
        Number.isInteger(c.appliquee) && c.appliquee >= 0 ? c.appliquee : null,
      portee:
        c.portee && typeof c.portee === "object"
          ? {
              type: String(c.portee.type || ""),
              id: c.portee.id == null ? null : String(c.portee.id),
            }
          : { type: "", id: null },
      verrouillee: c.verrouillee === true,
    });
  }
  return {
    consignes,
    compteMotif: estTexte(d.compte_motif, 300) ? d.compte_motif : null,
  };
}

/** « appliquée 3 fois », « jamais appliquée », ou « pas encore compté ». */
export function libelleApplication(n) {
  if (n === null || n === undefined) return "pas encore compté";
  if (n === 0) return "jamais appliquée";
  return `appliquée ${n} fois`;
}

export default function Consignes({ titre, donnees, onOublier, noms }) {
  const v = validerConsignes(donnees);
  if (v.erreur) return <PhraseAbsence titre={titre} texte={v.erreur} />;
  if (v.vide)
    return (
      <PhraseAbsence titre={titre} texte="je n'ai retenu aucune consigne" />
    );
  const { consignes, compteMotif } = v;
  const peutOublier = typeof onOublier === "function";
  const nommer = (id) => (noms && noms[id]) || id;
  const porteeDe = (p) => {
    if (p.type === "partout") return "partout";
    if (p.type === "logement" && p.id) return nommer(p.id);
    if (p.type === "proprio") return "un propriétaire";
    return null;
  };
  const conclusion = `${titre} : ${consignes.length} consigne${consignes.length > 1 ? "s" : ""}`;

  return (
    <figure
      data-testid="dessin-consignes"
      aria-label={conclusion}
      style={{ margin: "6px 0 10px", padding: 0, maxWidth: 560 }}
    >
      <figcaption
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: COULEUR.navyD,
          margin: "0 0 4px",
        }}
      >
        {titre}
      </figcaption>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {consignes.map((c, i) => {
          const portee = porteeDe(c.portee);
          return (
            <li
              key={c.id}
              data-testid="consigne-ligne"
              data-appliquee={
                c.appliquee === null ? "inconnu" : String(c.appliquee)
              }
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: "0 10px",
                padding: "6px 8px",
                borderTop: i === 0 ? "none" : `1px solid ${COULEUR.bord}`,
              }}
            >
              <span>
                <span
                  data-testid="consigne-texte"
                  style={{ fontSize: 13, color: COULEUR.navyD }}
                >
                  {c.texte}
                </span>
                <span
                  data-testid="consigne-vie"
                  style={{
                    display: "block",
                    fontSize: 11.5,
                    color: COULEUR.gris,
                    marginTop: 2,
                  }}
                >
                  {[
                    c.pose_le_heure ? `retenue le ${c.pose_le_heure}` : null,
                    portee ? `· ${portee}` : null,
                    `· ${libelleApplication(c.appliquee)}`,
                    c.verrouillee ? "· verrouillée" : null,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              </span>
              {peutOublier && (
                <button
                  type="button"
                  data-testid="consigne-oublier"
                  aria-label={`Oublier : ${c.texte.slice(0, 60)}`}
                  title="Lui faire oublier cette consigne"
                  onClick={() => onOublier(c.id)}
                  style={{
                    alignSelf: "start",
                    background: "transparent",
                    border: `1px solid ${COULEUR.bord}`,
                    borderRadius: 4,
                    color: COULEUR.grisD,
                    cursor: "pointer",
                    fontSize: 14,
                    lineHeight: 1,
                    padding: "2px 6px",
                  }}
                >
                  ×
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {compteMotif && (
        <p
          data-testid="consignes-compte-motif"
          style={{
            margin: "4px 0 0",
            fontSize: 11.5,
            color: COULEUR.gris,
            fontStyle: "italic",
          }}
        >
          {compteMotif}
        </p>
      )}
    </figure>
  );
}
